import { useEffect, useSyncExternalStore } from "react";

/**
 * ato.run's own chrome theme: Light, Dark or System (the default).
 *
 * Only the platform UI follows it. Apps keep their own colours: app frames
 * are rendered with `color-scheme: light` (see discovery.css), so an app
 * sees the same `prefers-color-scheme` and canvas it always has, and no
 * filter or injected style ever reaches it.
 *
 * `index.html` applies the stored choice before the first paint; this
 * module keeps it applied and follows the OS while the choice is System.
 */
export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "ato.theme.v1";
const DARK_QUERY = "(prefers-color-scheme: dark)";
const listeners = new Set<() => void>();

function readPreference(): ThemePreference {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    return value === "light" || value === "dark" ? value : "system";
  } catch {
    return "system";
  }
}

let preference: ThemePreference = typeof window === "undefined" ? "system" : readPreference();

function systemTheme(): ResolvedTheme {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(DARK_QUERY).matches
    ? "dark"
    : "light";
}

export function resolveTheme(value: ThemePreference = preference): ResolvedTheme {
  return value === "system" ? systemTheme() : value;
}

/** Sets `data-theme` and the root colour scheme; nothing else changes. */
export function applyTheme(value: ThemePreference = preference) {
  if (typeof document === "undefined") return;
  const resolved = resolveTheme(value);
  const root = document.documentElement;
  if (root.dataset.theme !== resolved) root.dataset.theme = resolved;
  root.style.colorScheme = resolved;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", resolved === "dark" ? "#0f1117" : "#ffffff");
}

export function setThemePreference(value: ThemePreference) {
  preference = value;
  try {
    if (value === "system") localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, value);
  } catch {
    // Device-local preference; the theme still changes for this visit.
  }
  applyTheme(value);
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** The chosen preference; keeps System in step with the OS. */
export function useThemePreference(): [ThemePreference, (value: ThemePreference) => void] {
  const value = useSyncExternalStore(subscribe, () => preference, () => "system" as ThemePreference);
  useEffect(() => {
    applyTheme(value);
    if (value !== "system" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(DARK_QUERY);
    const follow = () => applyTheme("system");
    query.addEventListener("change", follow);
    return () => query.removeEventListener("change", follow);
  }, [value]);
  return [value, setThemePreference];
}
